import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { PhotoProofCapture } from './PhotoProofCapture';
import { clockStateManager, type ClockState } from '../utils/clockState';
import { type ScreenType } from '../types';
import { 
  MapPin, 
  Wifi, 
  Camera,
  Play,
  Square,
  Coffee,
  CheckCircle
} from 'lucide-react';

interface ClockItClockInOutProps {
  user?: any;
  onClockIn?: () => Promise<void>;
  onClockOut?: () => Promise<void>; 
  onNavigate?: (screen: ScreenType) => void;
}

export function ClockItClockInOut({ user, onClockIn, onClockOut, onNavigate }: ClockItClockInOutProps = {}) {
  // Use shared clock state
  const [clockState, setClockState] = useState<ClockState>(clockStateManager.getState());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [pendingAction, setPendingAction] = useState<'clockin' | 'clockout' | 'addphoto' | null>(null);

  // Extract state values for easier access
  const {
    isClockedIn,
    workStartTime,
    isOnBreak,
    breakStartTime,
    isOnLunch,
    lunchStartTime,
    hasPhotoProof,
    clockInPhoto,
    clockOutPhoto,
  } = clockState;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const unsubscribe = clockStateManager.subscribe(setClockState);

    return () => {
      clearInterval(timer);
      unsubscribe();
    };
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDuration = (startTime: string | Date) => {
    const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getWorkDuration = () => {
    if (!workStartTime) return '0h 0m';
    return formatDuration(workStartTime);
  };

  const getBreakDuration = () => {
    if (!breakStartTime) return '0m';
    return formatDuration(breakStartTime);
  };

  const getLunchDuration = () => {
    if (!lunchStartTime) return '0m';
    return formatDuration(lunchStartTime);
  };

  const handlePhotoSuccess = (photoUrl: string) => {
    console.log('📸 Photo captured successfully for action:', pendingAction);

    if (pendingAction === 'clockin') {
      clockStateManager.clockIn(photoUrl);
    } else if (pendingAction === 'clockout') {
      clockStateManager.clockOut(photoUrl);
    } else if (pendingAction === 'addphoto') {
      clockStateManager.addPhotoToCurrentSession(photoUrl);
    }

    setShowPhotoCapture(false);
    setPendingAction(null);
  };

  const handlePhotoCancel = () => {
    setShowPhotoCapture(false);
    setPendingAction(null);
  };

  const handleClockInWithPhoto = () => {
    setPendingAction('clockin');
    setShowPhotoCapture(true);
  };

  const handleClockOutWithPhoto = () => {
    setPendingAction('clockout');
    setShowPhotoCapture(true);
  };

  const handleAddPhoto = () => {
    setPendingAction('addphoto');
    setShowPhotoCapture(true);
  };

  if (showPhotoCapture) {
    return (
      <PhotoProofCapture
        onPhotoTaken={handlePhotoSuccess}
        onSkip={handlePhotoCancel}
        action={pendingAction || 'addphoto'}
        title={
          pendingAction === 'clockin' ? 'Clock In Photo' :
          pendingAction === 'clockout' ? 'Clock Out Photo' :
          'Add Documentation Photo'
        }
        subtitle={
          pendingAction === 'clockin' ? 'Take a photo to document the start of your work shift' :
          pendingAction === 'clockout' ? 'Take a photo to document the end of your work shift' :
          'Add a photo to strengthen your work session documentation'
        }
      />
    );
  }

  return (
    <div className="pb-20 bg-background min-h-screen">
      {/* Status Header */}
      <div className="px-4 pt-4">
        <div className="clock-it-card card-content">
          <div className="text-center">
            <div className="text-large text-foreground mb-2">
              {formatTime(currentTime)}
            </div>
            <div className="text-caption mb-4">
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isClockedIn ? 'bg-success animate-pulse' : 'bg-muted'}`}></div>
                <span className="text-sm font-medium">
                  {isClockedIn ? 'Clocked In' : 'Clocked Out'}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Main Office</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Wifi className="w-4 h-4 text-success" />
                <span className="text-sm text-success">Connected</span>
              </div>
            </div>

            {/* Work Duration Display */}
            {isClockedIn && (
              <div className="bg-primary/10 rounded-lg p-4 mb-4">
                <div className="text-2xl font-bold text-primary">
                  {getWorkDuration()}
                </div>
                <div className="text-sm text-primary/80">
                  Total work time today
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photo Proof Status */}
      <div className="px-4 py-4">
        {isClockedIn && !hasPhotoProof && (
          <Alert className="border-primary bg-primary/10 mb-4">
            <Camera className="h-4 w-4 text-primary" />
            <AlertDescription className="text-primary">
              <div className="flex items-center justify-between">
                <div>
                  <strong>Strengthen your records!</strong> Add a photo for better documentation.
                </div>
                <Button
                  onClick={handleAddPhoto}
                  size="sm"
                  className="ml-3 bg-primary hover:bg-primary/90 text-white h-8 px-3 text-xs"
                >
                  <Camera className="w-3 h-3 mr-1" />
                  Add Photo
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {hasPhotoProof && (
          <Alert className="border-success bg-success/10 mb-4">
            <CheckCircle className="h-4 w-4 text-success" />
            <AlertDescription className="text-success">
              <strong>Excellent!</strong> You have photo documentation for this session.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Main Action Section */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content text-center">
          <div className="mb-6">
            <div className="flex justify-center mb-4">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                isClockedIn ? 'bg-success/20' : 'bg-primary/20'
              }`}>
                {isClockedIn ? (
                  <Square className={`w-12 h-12 ${isOnBreak || isOnLunch ? 'text-warning' : 'text-success'}`} />
                ) : (
                  <Play className="w-12 h-12 text-primary" />
                )}
              </div>
            </div>

            <h2 className="text-xl font-semibold mb-2">
              {isClockedIn ? 'Currently Working' : 'Ready to Start'}
            </h2>
            
            {isClockedIn && (
              <p className="text-muted-foreground mb-4">
                Started at {workStartTime ? new Date(workStartTime).toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                }) : '--:--'}
              </p>
            )}
          </div>

          {/* Primary Action Button */}
          <div className="space-y-3">
            {!isClockedIn ? (
              <>
                <Button
                  onClick={handleClockInWithPhoto}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-14 text-lg font-semibold shadow-lg shadow-primary/25 transition-all duration-200 active:scale-95"
                >
                  <Camera className="w-6 h-6 mr-3" />
                  Take Photo & Clock In
                </Button>
                <Button
                  onClick={() => clockStateManager.clockIn()}
                  variant="outline"
                  className="w-full rounded-full h-12 text-base"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Clock In (Skip Photo)
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleClockOutWithPhoto}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-14 text-lg font-semibold shadow-lg shadow-primary/25 transition-all duration-200 active:scale-95"
                >
                  <Camera className="w-6 h-6 mr-3" />
                  Take Photo & Clock Out
                </Button>
                <Button
                  onClick={() => clockStateManager.clockOut()}
                  variant="outline"
                  className="w-full rounded-full h-12 text-base"
                >
                  <Square className="w-5 h-5 mr-2" />
                  Clock Out (Skip Photo)
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Break/Lunch Controls - Only show when clocked in */}
      {isClockedIn && (
        <div className="px-4 mb-6">
          <div className="clock-it-card card-content">
            <h3 className="font-semibold text-center mb-4">Break Management</h3>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  if (isOnBreak) {
                    clockStateManager.endBreak();
                  } else {
                    clockStateManager.startBreak();
                  }
                }}
                variant={isOnBreak ? "default" : "outline"}
                className={`rounded-full h-12 transition-all duration-200 ${
                  isOnBreak 
                    ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' 
                    : 'hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'
                } ${isOnLunch ? 'opacity-50' : ''}`}
                disabled={isOnLunch}
              >
                <Coffee className="w-5 h-5 mr-2" />
                {isOnBreak ? 'End Break' : 'Start Break'}
              </Button>
              
              <Button
                onClick={() => {
                  if (isOnLunch) {
                    clockStateManager.endLunch();
                  } else {
                    clockStateManager.startLunch();
                  }
                }}
                variant={isOnLunch ? "default" : "outline"}
                className={`rounded-full h-12 transition-all duration-200 ${
                  isOnLunch 
                    ? 'bg-orange-500 hover:bg-orange-600 text-white animate-pulse' 
                    : 'hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700'
                } ${isOnBreak ? 'opacity-50' : ''}`}
                disabled={isOnBreak}
              >
                <span className="mr-2">🍽️</span>
                {isOnLunch ? 'End Lunch' : 'Start Lunch'}
              </Button>
            </div>

            {/* Current break/lunch duration */}
            {(isOnBreak || isOnLunch) && (
              <div className="mt-4 text-center">
                <Badge 
                  variant="secondary" 
                  className={`text-sm ${
                    isOnLunch 
                      ? 'bg-orange-500 text-white border-orange-500' 
                      : 'bg-amber-500 text-white border-amber-500'
                  }`}
                >
                  {isOnBreak && `☕ Break: ${getBreakDuration()}`}
                  {isOnLunch && `🍽️ Lunch: ${getLunchDuration()}`}
                </Badge>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo Documentation Status */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <h3 className="font-semibold text-center mb-4">Documentation Status</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${clockInPhoto ? 'bg-success' : 'bg-muted'}`}></div>
                <span className="text-sm">Clock In Photo</span>
              </div>
              {clockInPhoto ? (
                <Badge className="status-verified text-xs">✓ Captured</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Not taken</Badge>
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${clockOutPhoto ? 'bg-success' : 'bg-muted'}`}></div>
                <span className="text-sm">Clock Out Photo</span>
              </div>
              {clockOutPhoto ? (
                <Badge className="status-verified text-xs">✓ Captured</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Not taken</Badge>
              )}
            </div>
          </div>

          {isClockedIn && !hasPhotoProof && (
            <div className="mt-4">
              <Button
                onClick={handleAddPhoto}
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary hover:text-white"
              >
                <Camera className="w-4 h-4 mr-2" />
                Add Photo Documentation
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}